import React, {useEffect, useMemo, useRef, useState} from 'react'
import tw from 'tailwind-styled-components'
import {Lang} from 'yougile-lang'
import {HTMLUtil, Util} from 'yougile-util'
import {DataObjectId, Hub, repeatTemplatesContainerId} from 'yougile-model'
import {ApiUi, UiSlot} from '@api/components'
import {Items} from '@api/model/items'
import {PermissionsChecker} from '@client/permissions-checker'
import {SharedBoardsClient} from '@client/shared-boards-client'
import {Sync} from '@client/sync'
import {UserSettings} from '@client/user-settings'
import {UtilUi} from '@common/utils-ui'
import {DealColumnSelector} from '@components/chat/chat-header/deal-column-selector'
import {TaskChatQuickLinkList} from '@components/chat/chat-header/task-chat-quick-link-list'
import {IconButton, LinkButton} from '@components/common/base/buttons'
import {Icon} from '@components/common/base/icon'
import {TabData, TabsBase} from '@components/common/base/tabs'
import {TextLoader} from '@components/common/base/text-loader'
import {TextWithUrl} from '@components/common/base/text-with-url'
import {Tooltip} from '@components/common/tooltip/tooltip'
import {crmStore} from '@components/crm/crm-store'
import {DealSubtitle} from '@components/deal-subtitle/deal-subtitle'
import {IdTaskLinkRightPanel} from '@components/id-tasks/id-task-link-right-panel'
import {getAllTasksFromPrivateLists} from '@components/my-tasks/utils'
import {rightPanelStore} from '@components/right-panel/right-panel'
import {chatsCache} from '@components/right-panel/task-chat-list/ensure-cache-initialized'
import {StickerItem} from '@components/sticker'
import {DoneButton} from '@components/task-card/done-button'
import {
  dealMenuVisibleItems,
  TaskMenuNew,
  TaskMenuVisibleItems
} from '@components/task-menu/task-menu-new'
import {createEnhancedWithSyncedFullTaskChat} from '@containers/enhanced-with-full-task-chat/enhanced-with-synced-full-task-chat'
import {
  keyboardHandler,
  textCursorSelectAll,
  useInput,
  useLoadedLazyTaskEffect
} from '@containers/hooks'
import {cn} from '@containers/html-utils'
import {TaskChatTab} from '@extensions/app-persistence'
import {ChatUtils} from '@extensions/chat-utils'
import {EmojiUtils} from '@extensions/emoji-utils'
import {isMirroredHub} from '@extensions/mirrored-tasks/is-mirrored-hub'
import {FullTaskChatProxy} from '@extensions/proxies/full-task-chat-proxy'
import {Sticker} from '@extensions/sticker/sticker'
import {SubtaskUtil} from '@extensions/subtask-util'
import {Task} from '@extensions/task'
import {MessageBox} from '../../message-box'
import {useTaskFromHubWithEffect} from '../../my-tasks/task-item'
import {ChatNav} from './chat-nav'

const TwTaskHeader = tw.div`
  px-24
  pb-8
  flex
  flex-col
  gap-8
`

const TwRow = tw.div`
  flex
  justify-between
  items-start
  gap-8
`

const TwHeading = tw(TextWithUrl)`
  min-w-0
  flex-1
  text-18
  leading-6
  font-semibold
  text-primary
  break-words
`

const TwHeadingEditing = tw.textarea`
  box-content
  m-0
  p-0
  -mb-4
  pb-4
  !min-h-24
  !max-h-172
  !border-none
  !resize-none
  !bg-transparent
  !w-initial
  flex-1
  !shadow-border-b-1
  !shadow-grey-l60
  text-18
  leading-6
  font-semibold
  text-primary
  break-words
  whitespace-pre-wrap
`

const TwText = tw.div`
  plain-text-regular
  text-placeholder
`

interface TaskChatHeaderProps extends FullTaskChatProxy {
  readonly companyId: DataObjectId
}

// eslint-disable-next-line max-lines-per-function
const BaseTaskChatHeaderBase = (props: TaskChatHeaderProps) => {
  const {chatName, newMessagesAmount, hasItems, complete, chatId} = props
  const currentTabRaw = rightPanelStore.currentTab
  const box = Sync.get(props)
  const hub = box.get(chatId) as Hub | undefined
  const mirror = isMirroredHub(hub)

  const isDeal = crmStore.isDeal(hub)
  const isOrganization = crmStore.getCrmRoleOfHub(hub) === 'organization'
  const isContactPerson = crmStore.getCrmRoleOfHub(hub) === 'contact'

  const isPrivate = !!hub?.data?.private
  const isFromPrivateList = useMemo(() => {
    if (hub?.id && isPrivate) {
      const parents = SubtaskUtil.getParentTaskIds(hub.id)
      const allTasksFromPrivateLists = getAllTasksFromPrivateLists()
      return parents.length
        ? parents.some(parentId => allTasksFromPrivateLists.includes(parentId))
        : getAllTasksFromPrivateLists().includes(hub.id)
    }
    return false
  }, [hub?.id, isPrivate])
  const isRepeatTemplate = hub?.location === repeatTemplatesContainerId
  const currentTab: TaskChatTab =
    currentTabRaw === 'chat' && isRepeatTemplate ? 'description' : currentTabRaw

  const unarchive = () => {
    if (!PermissionsChecker.checkArchive(hub)) {
      MessageBox.warn(Lang.msg.permissionsForbiddenTaskClose)
      return
    }
    Task.forceChangeCompleteness(hub, false)
  }
  const makePublic = () => {
    delete hub.data.private
    hub.commit()
  }
  useLoadedLazyTaskEffect(hub, 'task-chat-header')
  const task = useTaskFromHubWithEffect(box, hub)

  const toggleStar = () => {
    UserSettings.setStarred(task, !task.starred, 'task')
    chatsCache.clear()
    ChatUtils.resetCache('cachedTasksGroupedByUserPresence')
  }

  const isShared = SharedBoardsClient.isGuest()

  const tabs: TabData[] = isDeal
    ? [
        {
          id: 'dealHistory',
          title: Lang.msg.crm.rightPanel.dealHistory.title
        },
        {
          id: 'aboutClient',
          title: Lang.msg.crm.rightPanel.contactPerson.title
        },
        {
          id: 'chat',
          title: Lang.msg.chatTabChat,
          ...(currentTab !== 'chat' && newMessagesAmount > 0
            ? {badgeUnread: true, badgeContent: `+${newMessagesAmount}`}
            : {dot: hasItems?.chat})
        }
      ]
    : [
        {
          id: 'chat',
          title: Lang.msg.chatTabChat,
          ...(currentTab !== 'chat' && newMessagesAmount > 0
            ? {badgeUnread: true, badgeContent: `+${newMessagesAmount}`}
            : {dot: hasItems?.chat})
        },
        {
          id: 'connected',
          title: Lang.msg.chat.taskTab
        },
        {
          id: 'description',
          title: Lang.msg.chatTabDescription,
          dot: hasItems?.description
        },
        {
          id: 'checklists',
          title: Lang.msg.chatTabSubtasks,
          dot: hasItems?.checklists
        }
      ]

  let taskMenuVisibleItems: TaskMenuVisibleItems[]

  if (isDeal) {
    taskMenuVisibleItems = dealMenuVisibleItems.filter(
      item => item !== 'select-multiple'
    )
  }

  const [localChatName, setLocalChatName] = useState(chatName)
  useEffect(() => {
    setLocalChatName(chatName)
  }, [chatName, setLocalChatName])

  const [renaming, setRenaming] = React.useState(false)
  const [newName, onChange, setNewName] = useInput('')
  const startRename = () => {
    if (PermissionsChecker.checkRename(hub)) {
      setNewName(chatName)
      setRenaming(true)
    }
  }
  const stopRename = () => {
    setRenaming(false)
    return true
  }
  const saveName = () => {
    const name = HTMLUtil.trimLineBreaks(newName)
    if (name && name !== chatName) {
      const hub = box.getHub(task.id)
      if (hub && Items.rename(Items.fromHub(task), name)) {
        box.editTitle(hub, name)
        setLocalChatName(name)
      }
    }
    stopRename()
  }
  const keyDown = keyboardHandler({enter: saveName, escape: stopRename})

  const editingRef = useRef<HTMLElement>()
  const headingRef = useRef<HTMLElement>()
  const [editingHeight, setEditingHeight] = useState<number | undefined>(
    undefined
  )
  useEffect(() => {
    if (editingRef.current && renaming) {
      const {width} = editingRef.current.getBoundingClientRect()
      const {height} = UtilUi.calcTextBounds(newName, {
        width: `${width}px`,
        fontSize: '18px',
        lineHeight: ' 24px',
        fontWeight: '600',
        overflowWrap: 'break-word'
      })
      setEditingHeight(height)
    } else {
      setEditingHeight(headingRef.current?.getBoundingClientRect().height)
    }
  }, [renaming, newName, editingRef, headingRef])

  if (!task || !hub) {
    return null
  }

  const softLimit = 150
  const hardLimit = 1024
  const titleFromHubOrSubtasks = !isRepeatTemplate
    ? Task.getTitle(task.id)
    : undefined
  const realTitle = titleFromHubOrSubtasks || localChatName || Lang.msg.unnamed
  const tooltipTitle = Util.limitStr(realTitle, hardLimit)
  const hideTooltip = realTitle.length <= softLimit

  return (
    <>
      <ChatNav />

      <TwTaskHeader>
        <div>
          <TwRow>
            <div className="flex flex-1 gap-8">
              {!isDeal && (
                <DoneButton
                  hub={hub}
                  className="!h-24"
                  fromWhere="right-panel"
                />
              )}
              {renaming ? (
                <TwHeadingEditing
                  ref={editingRef}
                  autoFocus={true}
                  value={newName}
                  onChange={onChange}
                  onKeyDown={keyDown}
                  onFocus={textCursorSelectAll}
                  onBlur={saveName}
                  style={{height: editingHeight}}
                />
              ) : (
                <div>
                  {mirror && (
                    <div
                      className="mr-8 min-h-16 min-w-16 relative"
                      style={{display: 'inline-block'}}
                    >
                      <Icon
                        className="absolute top-1 left-0"
                        icon="IconCustomMirroredTask"
                      />
                    </div>
                  )}

                  <Tooltip
                    offset={[0, 10]}
                    placement="bottom"
                    content={
                      <span className="break-words">
                        {EmojiUtils.safeParse(tooltipTitle, true)}
                      </span>
                    }
                    disabled={hideTooltip}
                  >
                    <span>
                      {hub.isLazy && <TextLoader />}
                      {!hub.isLazy && (
                        <TwHeading
                          ref={headingRef}
                          onDoubleClick={startRename}
                          text={realTitle}
                          bottom={-1}
                          limit={softLimit}
                        />
                      )}
                    </span>
                  </Tooltip>
                </div>
              )}
            </div>

            <div className="flex items-center gap-4">
              {!isRepeatTemplate && !isShared && (
                <>
                  {!isFromPrivateList &&
                    !isOrganization &&
                    !isContactPerson && (
                      <IconButton
                        icon={task.starred ? 'IconStarFilled' : 'IconStar'}
                        color="secondary"
                        size="small"
                        wrapperSize="big"
                        className={cn(
                          'ml-auto',
                          'mr-8',
                          task.starred && '!text-marker hover:!text-marker-l20'
                        )}
                        onClick={toggleStar}
                        content={Lang.context.common.starHint}
                      />
                    )}
                  <TaskMenuNew
                    task={task}
                    rename={startRename}
                    visibleItems={taskMenuVisibleItems}
                    className={cn('-mx-4', isFromPrivateList && 'ml-auto')}
                    fromWhere="right-panel"
                  />
                </>
              )}
              <ApiUi category="task-header-menu" id={hub.id} />
            </div>
          </TwRow>

          {(isDeal || isOrganization || isContactPerson) && (
            <DealSubtitle deal={hub} />
          )}
        </div>

        <IdTaskLinkRightPanel
          task={hub}
          className={`mt-[-3px] ${isDeal ? '' : 'pl-24'}`}
        />

        {isDeal && !isOrganization && !isContactPerson && (
          <div className="flex flex-wrap gap-4 pt-4">
            {Sticker.getAllPinned(hub)
              .filter(s => Sticker.getType(s) !== 'user')
              .map(sticker => (
                <StickerItem
                  key={sticker.id}
                  sticker={sticker}
                  location="task"
                  taskId={hub.id}
                  currentValue={hub.stickers?.[sticker.id] ?? ''}
                  withTooltip
                  withMenu
                />
              ))}
          </div>
        )}

        {isDeal && !isOrganization && !isContactPerson && (
          <DealColumnSelector deal={hub} />
        )}

        {complete && (
          <TwRow className="items-center">
            <TwText>{Lang.msg.taskCompleted}</TwText>
            <LinkButton
              onClick={unarchive}
              size="small"
              color="primary"
              noPaddings
            >
              {Lang.msg.menuUnarchive}
            </LinkButton>
          </TwRow>
        )}
        {isPrivate && (
          <>
            <TwRow>
              <TwText>{Lang.msg.chat.privateHint}</TwText>
            </TwRow>
            {!isFromPrivateList && (
              <TwRow>
                <LinkButton
                  onClick={makePublic}
                  size="small"
                  color="primary"
                  noPaddings
                >
                  {Lang.msg.chat.makePublic}
                </LinkButton>
              </TwRow>
            )}
          </>
        )}
        {isRepeatTemplate && (
          <TwRow>
            <TwText>{Lang.msg.repeat.templateHeader}</TwText>
          </TwRow>
        )}
        <ApiUi category="task-header-bottom" id={hub.id} />
        <UiSlot
          name="task-header-bottom"
          data={{
            chatId: hub.id
          }}
        />
      </TwTaskHeader>
      {!isOrganization && !isContactPerson && (
        <TabsBase
          tabs={tabs}
          onTabClick={id => rightPanelStore.setTab(id as TaskChatTab)}
          activeId={currentTab}
          className="px-24"
          slim
        />
      )}
      {currentTab === 'chat' && (
        <TaskChatQuickLinkList chatId={chatId} companyId={hub.companyId} />
      )}
    </>
  )
}

export const TaskChatHeader = createEnhancedWithSyncedFullTaskChat(
  BaseTaskChatHeaderBase
)
